import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

import { useAppContext } from '@/App.context'
import Page from '@/components/content/Page'
import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'
import { Translation } from '@/components/i18n/Translation'
import { LocalDate } from '@/components/LocalDate'
import { LocalDateTime } from '@/components/LocalDateTime'
import DataPoint, { DataPoints } from '@/components/primitives/DataPoint'
import { Product } from '@/components/ProductVisualizer'
import LoaderCircle from '@/components/util/LoaderCircle'
import { getDeviceOrder, getIsPending } from '@/ducks/deviceOrderReducer'
import withDeviceOrderLoaded from '@/hocs/withDeviceOrderLoaded'
import { useFormattedNumberWithCurrency } from '@/hooks/use-formatted-number-with-currency'
import { getFormattedOrgNumber, getFormatterMobileNumber } from '@/util/formatters/text'
import { DEVICE_ORDER_STATUS, getDeviceOrderStatusTextId } from '@/util/orders'

import DeviceOrderCommunications from './DeviceOrderCommunications'
import DeviceOrderDealerInstructions from './DeviceOrderDealerInstructions'
import DeviceOrderInputs from './DeviceOrderInputs'
import DeviceOrderOptions from './DeviceOrderOptions'

import './device-order-page.scss'

const DeviceOrderPage = () => {
  const params = useParams()
  const { whiteLabel } = useAppContext()
  const { formatNumber } = useFormattedNumberWithCurrency()
  // @ts-ignore
  const orderId = parseInt(params.order_id)
  const isPending = useSelector(getIsPending)
  const {
    status,
    name,
    code,
    email,
    department_name,
    department_code,
    last_edited_by,
    order_date,
    estimated_delivery,
    delivery_date,
    order_updated,
    total_amount,
    remainder_amount,
    down_payments,
    address,
    zip,
    city,
    products,
    customer_name,
    customer_org_no,
    currency,
  } = useSelector(getDeviceOrder)
  const emailParts = email?.split('@')

  return (
    <Page className="device-order-page">
      <h1>
        {/* @ts-ignore */}
        <Translation id="Device Order {orderId}" orderId={orderId} />
      </h1>

      {isPending ? (
        <LoaderCircle />
      ) : (
        <>
          <h2>
            <Translation id={getDeviceOrderStatusTextId(status, whiteLabel)} />
          </h2>
          {/* @ts-ignore */}
          <DeviceOrderOptions />

          <DeviceOrderDealerInstructions />

          <div className="sections">
            <section>
              <h3>
                <Translation id="User" />
              </h3>
              <DataPoints>
                <DataPoint header={<Translation id="Name" />} value={name} />

                <DataPoint
                  header={<Translation id="Phone number" />}
                  value={getFormatterMobileNumber(code)}
                />

                <DataPoint
                  header={<Translation id="Customer" />}
                  value={
                    <>
                      {customer_name} ({getFormattedOrgNumber(customer_org_no)})
                    </>
                  }
                />

                <DataPoint
                  header={<Translation id="Department" />}
                  value={`${department_code} ${department_name}`}
                />

                <DataPoint
                  header={<Translation id="E-mail" />}
                  value={
                    emailParts && (
                      <>
                        {emailParts[0]}
                        <wbr />@{emailParts[1]}
                      </>
                    )
                  }
                />

                <DataPoint
                  header={<Translation id="Address" />}
                  value={
                    <>
                      {address}
                      <br />
                      {zip}, {city}
                    </>
                  }
                />
              </DataPoints>
            </section>

            <section>
              <h3>
                <Translation id="Order" />
              </h3>

              <DataPoints>
                <DataPoint
                  header={<Translation id="Remainder / Total cost" />}
                  value={`${formatNumber({ amount: remainder_amount })} / ${formatNumber({
                    amount: total_amount,
                    currency,
                  })}`}
                />

                <DataPoint
                  header={<Translation id="Down payments" />}
                  value={
                    down_payments === 0 ? (
                      <Translation id="None" />
                    ) : (
                      <Translation
                        id="{downpaymentsAmount} of {monetaryAmount}"
                        downpaymentsAmount={down_payments}
                        monetaryAmount={formatNumber({
                          amount: remainder_amount / down_payments,
                          currency,
                        })}
                      />
                    )
                  }
                />

                <DataPoint
                  header={<Translation id="Order date" />}
                  value={<LocalDate date={order_date} />}
                />

                {status === DEVICE_ORDER_STATUS.DELIVERED ? (
                  <DataPoint
                    header={<Translation id="Delivery date" />}
                    value={estimated_delivery ? <LocalDate date={delivery_date} /> : '-'}
                  />
                ) : (
                  <DataPoint
                    header={<Translation id="Estimated delivery" />}
                    value={estimated_delivery ? <LocalDate date={estimated_delivery} /> : '-'}
                  />
                )}

                <DataPoint
                  header={<Translation id="Last update" />}
                  value={<LocalDateTime date={order_updated} />}
                />

                <DataPoint
                  header={<Translation id="Last update by" />}
                  value={last_edited_by || name}
                />
              </DataPoints>
            </section>

            <DeviceOrderInputs />

            <section>
              <h3>
                <Translation id="Products" />
              </h3>

              <table>
                <thead>
                  <tr>
                    <th colSpan={2}>
                      <Translation id="product" />
                    </th>

                    <th>
                      <Translation id="id" />
                    </th>

                    <th className="center">
                      <Translation id="price" />
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {Boolean(products) &&
                    // @ts-ignore
                    products.map(product => (
                      <tr key={product.product_number}>
                        <td>
                          <Product imgUrl={product.img_url} />
                        </td>

                        <td>{product.model}</td>

                        <td>{product.product_number}</td>

                        <td className="number">
                          <FormattedNumberWithCurrency amount={product.price} currency={currency} />
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
              {/* @ts-ignore */}
              <DeviceOrderCommunications />
            </section>
          </div>
        </>
      )}
    </Page>
  )
}

export default withDeviceOrderLoaded(DeviceOrderPage)
